import { couponService } from '@/services';
import { useContext, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import Card from '@/components/Card';
import DeleteConfirmModal from '@/modals/DeleteConfirmModal';
import { GlobalContext } from '@/context';
import Input from '@/components/Inputs';
import CouponConfigContext from '@/views/SuperAdmin/Coupons/context';
import { ViewGridIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { useForm } from '@/hooks';

const SKIP_FIELDS = ['created_at', 'updated_at', 'coupon_info'];

const BasicInfoSection = () => {
  const {
    params: { couponId },
  } = useRouteMatch();
  const history = useHistory();
  const { notify } = useContext(GlobalContext);
  const { currentCoupon, setLoading } = useContext(CouponConfigContext);

  const [formValues, handleFormChange] = useForm({});
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState('');

  useEffect(() => {
    handleFormChange(currentCoupon, true);
  }, [currentCoupon]);

  const handleRowFormChange = (event) => {
    setShowSaveButton(true);
    handleFormChange(event);
  };
  const handleSave = async () => {
    setErrors('');
    if (!formValues.name) {
      setErrors('Name is required');
      return;
    }
    if (!formValues.code) {
      setErrors('Code is required');
      return;
    }
    if (!formValues.amount) {
      setErrors('Amount is required');
      return;
    }

    const newValues = {};
    Object.keys(formValues).forEach((_key) => {
      if (SKIP_FIELDS.includes(_key) || _key.startsWith('react-select')) {
        return;
      } else {
        newValues[_key] = formValues[_key];
      }
    });

    try {
      await couponService.updateCoupon(couponId, newValues);
      notify({ type: 'success', message: 'Success!' });
      setShowSaveButton(false);
      history.push('/coupons');
    } catch (err) {
      notify({ type: 'error', message: err.message });
    }
  };

  const handleDelete = () => {
    setShowConfirm(true);
  };

  const onCloseDeleteConfirm = () => {
    setShowConfirm(false);
  };

  const onConfirmDelete = async () => {
    setShowConfirm(false);

    setLoading(true);

    try {
      await couponService.deleteCoupon(couponId);
      history.push('/coupons');
    } catch (err) {
      notify({ type: 'error', message: err.message });
    }
    setLoading(false);
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <div className="px-4 sm:px-0">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Edit Coupon
          </h3>
        </div>
        <div>
          <button
            type="button"
            className="button button-primary"
            onClick={handleSave}
            disabled={!showSaveButton}
          >
            Save
          </button>
          <button
            type="button"
            className="button button-danger ml-3"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </div>
      {errors && (
        <div className="Login__errors__error p-4 mb-4 bg-red-50 text-red-900 font-semibold rounded-xl">
          {errors}
        </div>
      )}
      <div>
        <form onChange={handleRowFormChange}>
          <div className="w-full flex flex-col space-y-2">
            <div className="flex space-x-4">
              <div className="w-2/3">
                <Input
                  type="text"
                  label="Display Name"
                  id="name"
                  value={formValues?.name || ''}
                  onChange={() => {}}
                />
              </div>
              <Input
                className="w-1/3"
                type="select"
                label="Is Active"
                id="active"
                value={`${formValues?.active}` || 'true'}
                onChange={() => {}}
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </Input>
            </div>
            <div className="flex space-x-4">
              <Input
                type="text"
                label="Code"
                id="code"
                value={formValues?.code || ''}
                onChange={() => {}}
              />
              <Input
                type="text"
                label="Amount (in dollars)"
                id="amount"
                value={formValues?.amount || ''}
                onChange={() => {}}
              />
            </div>
          </div>
        </form>
        <DeleteConfirmModal
          show={showConfirm}
          onCancel={onCloseDeleteConfirm}
          onConfirm={onConfirmDelete}
          title="Delete coupon"
          description="Are you sure that you want to delete this coupon?"
        />
      </div>
    </>
  );
};

const SECTIONS = [
  {
    label: 'Edit Coupon',
    component: <BasicInfoSection />,
  },
];

const ConfigureCouponView = () => {
  let {
    params: { couponId },
  } = useRouteMatch();

  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);
  const [currentCoupon, setCurrentCoupon] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { coupon_info } = await couponService.getCouponById(couponId);
      setCurrentCoupon(coupon_info);
      setCurrentSection(0);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  return (
    <CouponConfigContext.Provider
      value={{
        currentCoupon,
        setCurrentCoupon,
        loading,
        setLoading,
      }}
    >
      <div className="lg:grid lg:grid-cols-12 lg:space-x-5 lg:space-y-5">
        <aside className="py-6 px-2 sm:px-6 lg:py-0 lg:px-0 lg:col-span-3">
          <nav className="space-y-1">
            {SECTIONS.map((_section, index) => (
              <button
                key={_section.label}
                type="button"
                onClick={() => setCurrentSection(index)}
                className={classNames(
                  'w-full text-gray-900 hover:text-gray-900 hover:bg-gray-50 group rounded-md px-3 py-2 flex items-center text-sm font-medium',
                  {
                    'bg-white text-mmj-blue hover:text-indigo-700 hover:bg-white':
                      currentSection == index,
                  },
                )}
              >
                {_section.label}
              </button>
            ))}
          </nav>
        </aside>
        <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-9 ">
          <Card className="ConfigureStateView mb-4 relative">
            {loading && (
              <div className="absolute top-0 left-0 right-0 bottom-0 bg-white opacity-60 z-50 rounded flex justify-center items-center">
                <ViewGridIcon className="animate-spin h-6 w-6" />
              </div>
            )}
            {SECTIONS[currentSection] && SECTIONS[currentSection].component}
          </Card>
        </div>
      </div>
    </CouponConfigContext.Provider>
  );
};
export default ConfigureCouponView;
